import { useState, useMemo } from "react";

import type { MonitorAndProtectModelCampaignsCampaignListItemModel } from "@similarweb/pro-api-client";
import { EmptyState, Pagination } from "@similarweb/ui-components";
import { Flex, Stack } from "@similarweb/ui-components/foundations";

import { useTranslation } from "pro-features/components/WithTranslation";
import type { IAccountUser } from "app/sharing/SharingService";

import { CampaignPermissions } from "../../../services/asset-permissions";

import { CampaignMetricsCard } from "./campaign-metrics-card";

const ITEMS_PER_PAGE = 10;

interface ICampaignsListProps {
    campaigns: MonitorAndProtectModelCampaignsCampaignListItemModel[];
    usersMap: Record<string, IAccountUser> | undefined;
    onShare: (link: string) => void;
    searchQuery?: string;
}

export const CampaignsList = ({ campaigns, usersMap, onShare, searchQuery }: ICampaignsListProps) => {
    const translate = useTranslation();
    const [currentPage, setCurrentPage] = useState(1);

    const totalPages = Math.max(1, Math.ceil(campaigns.length / ITEMS_PER_PAGE));
    const validPage = Math.min(currentPage, totalPages);

    const paginatedCampaigns = useMemo(() => {
        const startIndex = (validPage - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        return campaigns.slice(startIndex, endIndex);
    }, [campaigns, validPage]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const showPagination = campaigns.length > ITEMS_PER_PAGE;

    if (campaigns.length === 0) {
        const isSearching = Boolean(searchQuery?.trim());
        return (
            <Flex
                paddingY="44px"
                justifyContent="center"
                alignItems="center"
                width="100%"
                height="300px"
            >
                <EmptyState
                    iconName={"no-data-tree"}
                    title={translate(
                        isSearching
                            ? "monitor_and_protect.homepage.empty_state.no_search_results.title"
                            : "monitor_and_protect.homepage.empty_state.title",
                    )}
                    subtitle={translate(
                        isSearching
                            ? "monitor_and_protect.homepage.empty_state.no_search_results.subtitle"
                            : "monitor_and_protect.homepage.empty_state.subtitle",
                    )}
                />
            </Flex>
        );
    }

    return (
        <Stack gap="24px">
            {paginatedCampaigns.map((campaign) => {
                const campaignPermissions = new CampaignPermissions(campaign);
                const sharedBy = usersMap?.[campaign?.assetPermissions?.ownerUserId]?.User ?? "unknown";
                return (
                    <CampaignMetricsCard
                        key={campaign.id}
                        campaign={campaign}
                        sharedByUserName={sharedBy}
                        isOwner={campaignPermissions.isOwner}
                        onShare={onShare}
                    />
                );
            })}
            {showPagination ? (
                <Flex justifyContent="flex-end">
                    <Pagination
                        page={validPage}
                        itemsCount={campaigns.length}
                        itemsPerPage={ITEMS_PER_PAGE}
                        handlePageChange={handlePageChange}
                        hasItemsPerPageSelect={false}
                    />
                </Flex>
            ) : null}
        </Stack>
    );
};
